import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ImageUploader, Toast, ImageViewer } from 'antd-mobile';
import { UploadFile } from 'antd-mobile/es/image-uploader';
import { getToken } from '@/libs/functions/getValue'
import requestApi from './api'
import styles from './index.scss'
import uuid from 'short-uuid'
import last from 'lodash/last'

// API配置
export type ApiConfig = {
  url: string; // 上传API地址
  method?: string; // 请求方法，默认为POST
  headers?: Record<string, string>; // 请求头
  paramName?: string; // 文件参数名，默认为file
  withCredentials?: boolean; // 是否携带凭证
  data?: Record<string, any>; // 额外的请求数据
  transformResponse?: (response: any) => string; // 转换响应数据，返回图片URL
};

// 插件配置选项
export type ApiImageUploaderProps = {
  multiple?: boolean; // 是否允许多图上传
  maxCount?: number; // 最大上传数量
  accept?: string; // 接受的文件类型
  value?: UploadFile[] // 已上传的文件列表
  onChange?: (files: UploadFile[]) => void; // 文件变化回调
  apiConfig?: ApiConfig; // API配置
  disabled?: boolean; // 是否禁用
  resultKey?: string; // 结果中图片URL的键名
  explain?: string; // 结果中图片URL的键名
  size?: number; // 大小
};

const _apiConfig = {
  url: '/api/business/product/upload/image',
  paramName: 'image', // 文件参数名
  withCredentials: true, // 携带凭证
  data: {
    'h5-token': getToken('h5Token') || '',
  },
};
/**
 * 支持API上传的图片上传插件
 */
const DpUploadV2: React.FC<ApiImageUploaderProps> = ({
  multiple = false,
  maxCount = 9,
  accept,
  value,
  onChange,
  apiConfig = _apiConfig,
  resultKey = 'url',
  size = 2,
  ...props
}) => {
  const setUrl = (type: string, url: string) => type?.toLowerCase()?.includes('pdf') ? 'https://oss-images.dragonpass.com.cn/business-saas/website/product/2025-05-22_145044/75a9ff78-3892-4897-b1b6-a637e9ebb3c6.png' : url

  const _list = useMemo(() => {
    if (value) {
      let _value = []
      if (Array.isArray(value)) {
        _value = value.map((item, i) => {
          const type: any = last((item?.source || item?.url || _)?.split('.'));
          if (typeof item === 'string') {
            return {
              md5: uuid.generate(),
              status: 'done',
              url: setUrl(type, item),
              source: item
            }
          }
          return {
            ...item,
            md5: item.uid || uuid.generate(),
            status: item.status || 'done',
            url: setUrl(type, item?.source || item.url),
            source: item?.source || item?.url,
          }
        })
      }
      return _value
    }
    return []

  }, [value])

  // 处理文件上传
  const handleUpload = useCallback(
    async (file: File): Promise<Record<string, any>> => {
      try {
        const response: any = await requestApi({
          uri: '/api/business/product/upload/image',
          method: 'POST',
          params: {
            image: file
          },
        })
        if (response?.code) {
          return {
            status: 'error',
            raw: file,
            url: '',
            md5: uuid.generate(),
          };
        }
        return {
          ...response,
          url: setUrl(response?.suffix, response?.fileUrl),
          source: response?.fileUrl,
          md5: uuid.generate(),
        };
      } catch (error: any) {
        console.error('上传错误:', error);
        throw error;
      }
    },
    [apiConfig, resultKey]
  );

  // 处理文件选择
  const handleChange = useCallback(
    async (newFileList: UploadFile[]) => {
      // 过滤出新增的文件
      const addedFiles = newFileList.filter((newFile) => !(value || []).some((file) => file.md5 === newFile.md5));
      // 如果没有新增文件，直接更新文件列表
      if (addedFiles.length === 0) {
        // setFileList(newFileList);
        onChange?.(newFileList);
        return;
      }
      // 处理新增文件上传
      const uploadingFiles = [..._list];
      const uploadPromises = addedFiles.map(async (file) => {
        // if (!file.raw) return file;
        // 更新状态为上传中
        const uploadingFileIndex = uploadingFiles.findIndex((f) => f.md5 === file.md5);
        if (uploadingFileIndex !== -1) { // 说明有
          uploadingFiles[uploadingFileIndex] = {
            ...uploadingFiles[uploadingFileIndex],
            status: 'uploading',
            percent: 0,
          };
          // setFileList([...uploadingFiles]);
        } else {
          uploadingFiles.push({
            ...file,
            status: 'uploading',
            percent: 0,
          })
        }
        // 可以增加二次上传
      });

      // 等待所有上传完成
      const updatedFiles = await Promise.all(uploadPromises);
      const finalFileList = [...uploadingFiles];
      // 更新文件列表
      updatedFiles.forEach((updatedFile) => {
        const index = finalFileList.findIndex(
          (f) => f.md5 === updatedFile?.md5
        );
        if (index !== -1) {
          finalFileList[index] = updatedFile;
        }
      });
      // setFileList(finalFileList);
      onChange?.(finalFileList);
    },
    [_list, onChange, handleUpload]
  );

  // 处理文件删除
  const handleRemove = useCallback(
    async (file: UploadFile) => {
      // 从列表中移除文件
      const newFileList = [..._list].filter((f) => f.md5 !== file.md5);
      // setFileList(newFileList);
      onChange?.(newFileList);
      return true;
    },
    [_list, onChange]
  );

  return (
    <div className={styles["api-uploader-container"]}>
      <ImageUploader
        value={_list}
        onChange={handleChange}
        onDelete={handleRemove}
        multiple={multiple}
        maxCount={maxCount}
        accept={accept}
        beforeUpload={(file) => {
          const minSize = size || 2
          if (file instanceof File && file.size > minSize * 1024 * 1024) { // 默认文件大小限制2M
            Toast.show({
              content: '文件限制大小' + `${minSize}M`
            })
            throw new Error('文件限制大小' + `${minSize}M`)
          }
          const type: any = last(file?.name?.split('.')); // 
          if (type && !accept?.split(',')?.includes(`.${type?.toLowerCase()}`)) {
            Toast.show({
              content: '不支持的格式',
            });
            throw new Error('不支持的格式')
          }
          return file; // 所有校验通过，允许上传
        }}
        upload={handleUpload}
        preview={false}
        onPreview={(index: number, item: any) => {
          if (item?.suffix?.toLowerCase()?.includes('pdf')) {
            const link = document.createElement('a')
            link.href = item?.source
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link);
          } else {
            ImageViewer.Multi.show({
              images: [item?.url],
              defaultIndex: 0
            })
          }
        }}
      />
      <div className={styles['dp-file-upload--explain']}>{props?.explain}</div>
    </div>
  );
};

DpUploadV2.defaultProps = {
  uploadFileKey: 'image',
  accept: '.png,.jpg,.jpeg,.pdf',
} as any

export default DpUploadV2;